/**
 * RenameAuditDialog.js
 *
 * Modal for AppAdmin to edit an audit's name and description (does NOT
 * touch any form data — purely metadata on the parent audit doc).
 *
 * Props:
 *   open      : bool
 *   audit     : { id, name, description, ... } — the audit being edited
 *   onClose   : () => void
 *   onRenamed : (auditId, { name, description }) => void
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, Button, CircularProgress, Alert, Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore as db } from './firebase.config';
import { useAuth } from '../auth/AuthContext';

const RenameAuditDialog = ({ open, audit, onClose, onRenamed }) => {
  const { currentUser } = useAuth();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill fields when the dialog opens with a new audit
  useEffect(() => {
    if (open && audit) {
      setName(audit.name || '');
      setDescription(audit.description || '');
      setError('');
    }
  }, [open, audit]);

  const handleClose = () => {
    if (saving) return;
    setError('');
    onClose();
  };

  const trimmedName = name.trim();
  const hasChanges =
    trimmedName !== (audit?.name || '').trim() ||
    description.trim() !== (audit?.description || '').trim();

  const canSave = trimmedName.length > 0 && hasChanges;

  const handleSave = async () => {
    if (!canSave || !audit?.id) return;
    setSaving(true);
    setError('');

    try {
      await updateDoc(doc(db, 'auditReports', audit.id), {
        name: trimmedName,
        description: description.trim(),
        updatedBy: currentUser?.email || '',
        updatedAt: serverTimestamp(),
      });
      onRenamed(audit.id, {
        name: trimmedName,
        description: description.trim(),
      });
    } catch (err) {
      console.error('Error renaming audit:', err);
      setError(
        'Could not save changes. ' +
        (err?.message || 'Please try again.')
      );
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <EditIcon fontSize="small" />
        Edit Audit Details
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Update the name or description for this audit. Form data is not affected.
        </DialogContentText>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            autoFocus
            label="Audit Name"
            placeholder="e.g. Steel Q1 2026, Cement plant – Turkey"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={saving}
            inputProps={{ maxLength: 100 }}
            helperText={`${name.length}/100`}
          />

          <TextField
            label="Description (optional)"
            placeholder="Brief notes about this audit, scope, reporting period, etc."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={saving}
            inputProps={{ maxLength: 500 }}
            helperText={`${description.length}/500`}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameAuditDialog;